import React, { useEffect, useMemo, useState } from "react";
import { PRODUCTS } from "./productsData";
import badge from "../../assets/badge.png";
import croppedlogo from "../../assets/cropped-merchant-marvels.jpg";
import eyeSlash from "../../assets/eye-slash-icon.png";
import eye from "../../assets/eye-icon.png";
import piggyBank from "../../assets/piggy-bank.png";
import {
  getSelectedProduct,
  saveSelectedProduct,
  getDraft,
  saveDraft,
  clearDraft,
  clearSelectedProduct,
} from "./session";
import "./onboarding-form.css";

/* ─── API base URL ─────────────────────────────────────────── */
const API = "https://applications.merchantmarvels.com";

/* ─── Read ?id= from URL ────────────────────────────────────── */
function getUrlId() {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("id");
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/* ─── API helpers ───────────────────────────────────────────── */
async function apiGet(id) {
  const res = await fetch(`${API}/api/application/${id}`);
  if (!res.ok) throw new Error(`Application ${id} not found`);
  return res.json();
}

async function apiCreate(form, product, currentStep) {
  const res = await fetch(`${API}/api/application`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ form, product, currentStep }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to save application");
  return data;
}

async function apiUpdate(id, form, product, currentStep, status) {
  const res = await fetch(`${API}/api/application/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ form, product, currentStep, status }),
  });
  if (!res.ok) throw new Error("Failed to update application");
  return res.json();
}

/* ─── Push ?id= into the URL without reloading ─────────────── */
function setUrlId(id) {
  const url = new URL(window.location.href);
  url.searchParams.set("id", id);
  window.history.replaceState({}, "", url.toString());
}

/* ─── validators ────────────────────────────────────────────── */
const emailRx = /^[A-Za-z0-9._+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;
const phoneDigits = (v) => (v || "").replace(/\D/g, "");
const zipRx = /^\d{5}(-\d{4})?$/;
const usDateRx = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-\d{4}$/;

/* ─── input formatters ──────────────────────────────────────── */
const digitsOnly = (v, max) =>
  (v || "").toString().replace(/\D/g, "").slice(0, max);

function formatUSPhone(v) {
  const s = (v || "").toString();
  const rawDigits = digitsOnly(s, 11);
  if (!rawDigits) return "";
  const typedPlusOne = s.trim().startsWith("+1");
  let d = rawDigits;
  if (typedPlusOne && d.startsWith("1")) d = d.slice(1);
  if (!typedPlusOne && d.length === 11 && d.startsWith("1")) d = d.slice(1);
  d = d.slice(0, 10);
  const a = d.slice(0, 3);
  const b = d.slice(3, 6);
  const c = d.slice(6, 10);
  let out = "+1";
  if (a) out += `(${a}`;
  if (a.length === 3) out += ")";
  if (b) out += ` ${b}`;
  if (c) out += `-${c}`;
  return out;
}

function formatEIN(v) {
  const d = digitsOnly(v, 9);
  const p1 = d.slice(0, 2);
  const p2 = d.slice(2, 9);
  if (d.length <= 2) return p1;
  return `${p1}-${p2}`;
}

function formatZip(v) {
  const d = digitsOnly(v, 9);
  const p1 = d.slice(0, 5);
  const p2 = d.slice(5, 9);
  if (d.length <= 5) return p1;
  return `${p1}-${p2}`;
}

function formatRouting(v) {
  return digitsOnly(v, 9);
}
function formatAccount(v) {
  return digitsOnly(v, 17);
}

function formatUSDate(v) {
  const d = digitsOnly(v, 8);
  const mm = d.slice(0, 2);
  const dd = d.slice(2, 4);
  const yyyy = d.slice(4, 8);
  if (d.length <= 2) return mm;
  if (d.length <= 4) return `${mm}-${dd}`;
  return `${mm}-${dd}-${yyyy}`;
}

/* ─── field validators ──────────────────────────────────────── */
function required(v) {
  return v?.toString().trim() ? null : "This field is required.";
}

function validEmail(v) {
  const value = (v || "").trim();
  if (!value) return "Email is required.";
  if (/\s/.test(value)) return "Email cannot contain spaces.";
  if (!/^[A-Za-z0-9._+-@]+$/.test(value))
    return "Enter a valid email. Only letters, numbers, ., _, -, and + are allowed.";
  const parts = value.split("@");
  if (parts.length !== 2) return "Email must contain a single @ symbol.";
  const [localPart, domainPart] = parts;
  if (!localPart) return "Email username is required before @.";
  if (!domainPart) return "Email domain is required after @.";
  if (!/^[A-Za-z0-9._+-]+$/.test(localPart))
    return "Only letters, numbers, ., _, -, and + are allowed before @.";
  if (localPart.startsWith(".") || localPart.endsWith(".") || localPart.includes(".."))
    return "Email username cannot start/end with a dot or contain consecutive dots.";
  if (!/^[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/.test(domainPart))
    return "Enter a valid email domain.";
  if (domainPart.includes("..")) return "Email domain cannot contain consecutive dots.";
  return emailRx.test(value) ? null : "Enter a valid email address.";
}

function validPhone(v) {
  let d = phoneDigits(v);
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  if (!d) return "Phone is required.";
  return d.length === 10 ? null : "Enter a valid phone number.";
}
function validZip(v) {
  if (!v?.trim()) return "ZIP is required.";
  return zipRx.test(v.trim()) ? null : "Enter a valid ZIP code.";
}
function validYear(v) {
  if (!v?.trim()) return "Year is required.";
  const n = Number(v);
  if (!Number.isInteger(n)) return "Enter a valid year.";
  if (n < 1900 || n > new Date().getFullYear()) return "Enter a realistic year.";
  return null;
}
function validMoney(v, label = "Value") {
  if (!v?.toString().trim()) return `${label} is required.`;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? null : `Enter a valid ${label.toLowerCase()}.`;
}
function validPercent(v) {
  if (!v?.toString().trim()) return "Ownership % is required.";
  const n = Number(v);
  if (!Number.isFinite(n)) return "Enter a valid percentage.";
  if (n <= 0 || n > 100) return "Ownership % must be 1–100.";
  return null;
}
function validDob(v) {
  if (!v?.trim()) return "Date of birth is required.";
  if (!usDateRx.test(v.trim())) return "Use MM-DD-YYYY format.";
  const [mm, dd, yyyy] = v.split("-").map(Number);
  const dt = new Date(yyyy, mm - 1, dd);
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd)
    return "Enter a valid date.";
  if (dt > new Date()) return "Date of birth cannot be in the future.";
  return null;
}
function validEIN(v) {
  const d = (v || "").replace(/\D/g, "");
  if (!d) return "Federal Tax ID is required.";
  return d.length === 9 ? null : "Federal Tax ID must be 9 digits.";
}

/* ABA routing checksum (mod-10 weighted sum) */
function abaChecksum(routing) {
  const d = (routing || "").replace(/\D/g, "");
  if (d.length !== 9) return false;
  const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  const sum = d.split("").reduce((acc, ch, i) => acc + Number(ch) * w[i], 0);
  return sum % 10 === 0;
}

function validRouting(v) {
  const d = (v || "").replace(/\D/g, "");
  if (!d) return "Routing number is required.";
  if (d.length !== 9) return "Routing number must be 9 digits.";
  if (!abaChecksum(d)) return "Invalid routing number — please double-check.";
  return null;
}
function validAccount(v) {
  const d = (v || "").replace(/\D/g, "");
  if (!d) return "Account number is required.";
  return d.length >= 6 ? null : "Account number looks too short.";
}
function validConfirmAccount(account, confirmAccount) {
  if (!confirmAccount?.toString().trim()) return "Confirm account number is required.";
  const a = digitsOnly(account, 17);
  const b = digitsOnly(confirmAccount, 17);
  if (!b || b.length < 6) return "Confirm account number looks too short.";
  if (a !== b) return "Account numbers do not match.";
  return null;
}

/* ─── default blank form ────────────────────────────────────── */
const BLANK_FORM = {
  businessName: "",
  businessLegalName: "",
  businessOwnerName: "",
  businessEmail: "",
  businessPhone: "",
  annualVolume: "",
  businessZip: "",
  dbaName: "",
  yearStarted: "",
  avgSale: "",
  entityType: "",
  productServiceSold: "",
  businessAddr1: "",
  businessAddr2: "",
  city: "",
  state: "",
  zip2: "",
  ownershipPercent: "",
  ownerFirstName: "",
  ownerLastName: "",
  ownerTitle: "",
  dob: "",
  ownerEmail: "",
  ownerPhone: "",
  homeAddr1: "",
  homeAddr2: "",
  homeCity: "",
  homeState: "",
  homeZip: "",
  ein: "",
  highPurchaseTicket: "",
  bankName: "",
  accountNumber: "",
  confirmAccountNumber: "",
  routingNumber: "",
  voidedCheckFile: null,
  driverLicenseFile: null,
};

/* ─── component ─────────────────────────────────────────────── */
export default function OnboardingForm({ onDone }) {
  const urlId = getUrlId();

  const [applicationId, setApplicationId] = useState(urlId);
  const [loading, setLoading] = useState(!!urlId);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const selectedFromSession = getSelectedProduct();
  const draft = getDraft();

  const [openPanel, setOpenPanel] = useState(draft?.step ?? 0);
  const [completedPanels, setCompletedPanels] = useState(
    () => new Set(Array.from({ length: draft?.step ?? 0 }, (_, i) => i))
  );
  const [product, setProduct] = useState(
    draft?.product ?? selectedFromSession ?? null
  );
  const [form, setForm] = useState(draft?.form ?? BLANK_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [panelErrors, setPanelErrors] = useState(new Set());

  /* new states for UX improvements */
  const [einSkip, setEinSkip] = useState(false);
  const [bankingSkip, setBankingSkip] = useState(false);
  const [savedToast, setSavedToast] = useState(false);

  const accordionSteps = useMemo(
    () => [
      { key: "business", title: "Let's get started" },
      { key: "qualify", title: "Business Information" },
      { key: "owner", title: "Signer - Owner Information" },
      { key: "banking", title: "Banking Information" },
      { key: "selectProduct", title: "Select a POS Solution" },
    ],
    []
  );

  const stepOpts = { einSkip, bankingSkip };

  /* ── Prefill from URL ?id= ───────────────────────────────── */
  useEffect(() => {
    if (!urlId) return;
    (async () => {
      try {
        const data = await apiGet(urlId);
        const prefillForm = { ...BLANK_FORM };
        const f = data.form ?? {};

        const step1Fields = [
          "businessName", "businessOwnerName", "businessEmail",
          "businessPhone", "businessZip",
        ];
        step1Fields.forEach((k) => { prefillForm[k] = f[k] ?? ""; });

        const step2Fields = [
          "businessLegalName", "dbaName", "yearStarted", "avgSale", "highPurchaseTicket",
          "entityType", "productServiceSold", "annualVolume", "businessAddr1", "businessAddr2",
          "city", "state", "zip2", "ein",
        ];
        const hasStep2 = step2Fields.some((k) => f[k]);
        if (hasStep2) step2Fields.forEach((k) => { prefillForm[k] = f[k] ?? ""; });

        const step3Fields = [
          "ownershipPercent", "ownerFirstName", "ownerLastName", "ownerTitle",
          "dob", "ownerEmail", "ownerPhone", "homeAddr1", "homeAddr2",
          "homeCity", "homeState", "homeZip", "ein", "bankName",
          "accountNumber", "confirmAccountNumber", "routingNumber",
        ];
        const hasStep3 = step3Fields.some((k) => f[k]);
        if (hasStep3) step3Fields.forEach((k) => { prefillForm[k] = f[k] ?? ""; });

        setForm(prefillForm);
        if (data.product) {
          setProduct(data.product);
          saveSelectedProduct(data.product);
        }
        setOpenPanel(0);
      } catch (err) {
        setServerError(`Could not load application: ${err.message}`);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Persist draft to session ───────────────────────────── */
  useEffect(() => {
    saveDraft({ step: openPanel, form, product });
  }, [openPanel, form, product]);

  useEffect(() => {
    if (selectedFromSession && !product) setProduct(selectedFromSession);
  }, [selectedFromSession, product]);

  const update = (patch) => setForm((p) => ({ ...p, ...patch }));

  const markTouched = (field) => {
    if (!field) return;
    setTouched((p) => (p[field] ? p : { ...p, [field]: true }));
  };

  const handleFieldBlur = (field) => {
    markTouched(field);
    const stepKey = accordionSteps[openPanel]?.key;
    if (!stepKey) return;
    setErrors(validateStep(stepKey, form, product, stepOpts));
  };

  const displayErrors = useMemo(() => {
    if (showAllErrors) return errors;
    const out = {};
    Object.keys(errors || {}).forEach((k) => {
      if (touched[k]) out[k] = errors[k];
    });
    return out;
  }, [errors, showAllErrors, touched]);

  /* ── Save / update in DB ────────────────────────────────── */
  async function persistStep(nextStep, status) {
    if (!applicationId) {
      const res = await apiCreate(form, product, nextStep);
      setApplicationId(res.id);
      setUrlId(res.id);
      return res.id;
    } else {
      await apiUpdate(applicationId, form, product, nextStep, status);
      return applicationId;
    }
  }

  /* ── Final submission ───────────────────────────────────── */
  const completeApplication = async () => {
    try {
      setSubmitting(true);
      await persistStep(accordionSteps.length, "submitted");
      clearDraft();
      clearSelectedProduct();
      window.location.href = "/thankyou";
    } catch (err) {
      setServerError(err?.message || "Something went wrong submitting the application.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Forward arrow ──────────────────────────────────────── */
  const handleForward = async () => {
    setServerError("");
    setShowAllErrors(true);
    const currentStepKey = accordionSteps[openPanel]?.key;
    const e = validateStep(currentStepKey, form, product, stepOpts);
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    try {
      setSubmitting(true);
      await persistStep(openPanel + 1);
      setCompletedPanels((prev) => new Set([...prev, openPanel]));
      setPanelErrors((prev) => {
        const next = new Set(prev);
        next.delete(openPanel);
        return next;
      });
      setOpenPanel((p) => p + 1);
      setTouched({});
      setShowAllErrors(false);
      setErrors({});
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2500);
    } catch (err) {
      setServerError(err?.message || "Could not save your progress.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Back arrow ─────────────────────────────────────────── */
  const handleBack = () => {
    setServerError("");
    setErrors({});
    setTouched({});
    setShowAllErrors(false);
    setOpenPanel((p) => Math.max(p - 1, 0));
  };

  /* ── Global submit (validates all panels) ──────────────── */
  const handleGlobalSubmit = async () => {
    setServerError("");
    const errPanels = new Set();
    let firstErrPanel = null;
    let firstErrErrors = {};

    accordionSteps.forEach((stepDef, idx) => {
      const e = validateStep(stepDef.key, form, product, stepOpts);
      if (Object.keys(e).length > 0) {
        errPanels.add(idx);
        if (firstErrPanel === null) {
          firstErrPanel = idx;
          firstErrErrors = e;
        }
      }
    });

    if (errPanels.size > 0) {
      setPanelErrors(errPanels);
      setOpenPanel(firstErrPanel);
      setErrors(firstErrErrors);
      setShowAllErrors(true);
      setTouched({});
      return;
    }

    await completeApplication();
  };

  /* ── Panel header click (navigate back to completed panels) */
  const handlePanelClick = (idx) => {
    if (idx === openPanel) return;
    if (completedPanels.has(idx) || idx < openPanel) {
      setOpenPanel(idx);
      setTouched({});
      setShowAllErrors(false);
      setErrors({});
    }
  };

  /* ── Re-validate on change ──────────────────────────────── */
  useEffect(() => {
    const stepKey = accordionSteps[openPanel]?.key;
    if (!stepKey) return;
    const shouldValidate = showAllErrors || Object.keys(touched).length > 0;
    if (!shouldValidate) return;
    setErrors(validateStep(stepKey, form, product, stepOpts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, openPanel, product, accordionSteps, showAllErrors, touched, einSkip, bankingSkip]);

  /* ── Loading state while prefilling from URL ────────────── */
  if (loading) {
    return (
      <div className="obPage">
        <div className="obMain" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <p style={{ fontSize: 20, color: "#666" }}>Loading application…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="obPage">
      {/* ── Progress saved toast ── */}
      {savedToast && (
        <div className="obSavedToast">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Progress saved
        </div>
      )}

      <header className="obTopbar">
        <div className="obTopbarInner">
          <div className="obBrand">
            <div className="obLogoDot">
              <img className="croppedlogo" src={croppedlogo} alt="Merchant Marvels Logo" />
            </div>
            <div className="obBrandText">
              <div className="obBrandName">Merchant Marvels</div>
              <div className="obBrandSub">0% Free Processing</div>
            </div>
          </div>

          <div className="obTopbarMeta">
            {product ? (
              <div className="obTopbarRight">
                <div className="obProdName">{product.name}</div>
                <div className="obProdPrice">
                  ${Number(product.priceNow).toFixed(2)}/mo{" "}
                  <span className="obStrike">${Number(product.priceWas).toFixed(2)}/mo</span>
                </div>
              </div>
            ) : (
              <div className="obTopbarRight obMuted">No product selected</div>
            )}
            <div className="obTopbarDivider" />
            <div className="obTopbarHelp">
              <div className="obHelpLabel">Need Help? Questions?</div>
              <a className="obHelpPhone" href="tel:+13022086030">+1 (302) 208-6030</a>
            </div>
          </div>
        </div>
      </header>

      <main className="obMain">
        <div className="obCard">
          {serverError ? <div className="obServerError">{serverError}</div> : null}

          <div className="obAccordion">
            {accordionSteps.map((stepDef, idx) => {
              const isOpen = openPanel === idx;
              const isCompleted = completedPanels.has(idx);
              const hasErr = panelErrors.has(idx);
              const isLast = idx === accordionSteps.length - 1;
              const isClickable = !isOpen && (isCompleted || idx < openPanel || hasErr);

              return (
                <div
                  key={stepDef.key}
                  className={`obAccPanel${isOpen ? " isOpen" : ""}${isCompleted && !hasErr ? " isCompleted" : ""}${hasErr ? " hasError" : ""}`}
                >
                  <button
                    type="button"
                    className={`obAccHeader${isClickable ? " isClickable" : ""}`}
                    onClick={() => handlePanelClick(idx)}
                  >
                    <span className="obAccNum">
                      {hasErr ? "!" : isCompleted && !isOpen ? "✓" : idx + 1}
                    </span>
                    <span className="obAccTitle">{stepDef.title}</span>
                    {hasErr && <span className="obAccErrBadge">Incomplete — please fix errors</span>}
                    <span className="obAccChevron">{isOpen ? "▼" : isClickable ? "▶" : ""}</span>
                  </button>

                  {isOpen && (
                    <div className="obAccProgressWrap">
                      <div className="obAccProgressMeta">
                        <span className="obAccProgressStepLabel">Step {idx + 1} of {accordionSteps.length}</span>
                        <span className="obAccProgressPct">{Math.round((idx + 1) / accordionSteps.length * 100)}% complete</span>
                      </div>
                      <div className="obAccProgressTrack">
                        <div
                          className="obAccProgressBar"
                          style={{ width: `${Math.round((idx + 1) / accordionSteps.length * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {isOpen && (
                    <div className="obAccBody">
                      {stepDef.key === "business" && (
                        <>
                          <p className="obSub obAccSub">
                            Please tell us about your business and see how much you could save with Merchant Marvels
                          </p>
                          <p className="obStep1Tagline">
                            Takes about 5 minutes &nbsp;·&nbsp; No credit check to apply &nbsp;·&nbsp; No commitment
                          </p>
                        </>
                      )}

                      {stepDef.key === "business" && (
                        <StepBusiness
                          form={form}
                          errors={displayErrors}
                          onChange={update}
                          onBlurField={handleFieldBlur}
                        />
                      )}
                      {stepDef.key === "qualify" && (
                        <StepQualify
                          form={form}
                          errors={displayErrors}
                          onChange={update}
                          onBlurField={handleFieldBlur}
                          einSkip={einSkip}
                          onEinSkipChange={setEinSkip}
                        />
                      )}
                      {stepDef.key === "owner" && (
                        <StepOwner
                          form={form}
                          errors={displayErrors}
                          onChange={update}
                          onBlurField={handleFieldBlur}
                        />
                      )}
                      {stepDef.key === "banking" && (
                        <StepBanking
                          form={form}
                          errors={displayErrors}
                          onChange={update}
                          onBlurField={handleFieldBlur}
                          bankingSkip={bankingSkip}
                          onBankingSkipChange={setBankingSkip}
                        />
                      )}
                      {stepDef.key === "selectProduct" && (
                        <StepSelectProduct
                          selected={product}
                          onSelect={(p) => {
                            setProduct(p);
                            saveSelectedProduct(p);
                            setErrors((prev) => {
                              const { product: _drop, ...rest } = prev;
                              return rest;
                            });
                          }}
                          error={errors.product}
                        />
                      )}

                      <div className="obAccNav">
                        {stepDef.key === "business" && (
                          <div className="obNavDisclaimer">
                            <div className="obSmsDisclaimer">
                              <svg className="obSmsIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                              </svg>
                              <span>We'll text you your application status. Reply <strong>STOP</strong> anytime to opt out.</span>
                            </div>
                            <div className="obTrustBadge">
                              <svg className="obTrustIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                <polyline points="9 12 11 14 15 10"/>
                              </svg>
                              <span><strong>We won't spam you.</strong> One way to reach you, one quote. No selling your info, no robocalls.</span>
                            </div>
                          </div>
                        )}
                        <div className="obAccArrows">
                          {idx > 0 && (
                            <button
                              type="button"
                              className="obArrowBtn obBackBtn"
                              onClick={handleBack}
                              disabled={submitting}
                              aria-label="Previous section"
                            >
                              ← Back
                            </button>
                          )}
                          {!isLast && (
                            <button
                              type="button"
                              className="obArrowBtn obArrowBtnFwd obContinueBtn"
                              onClick={handleForward}
                              disabled={submitting}
                              aria-label="Next section"
                            >
                              {submitting ? "Saving…" : "Continue →"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="obGlobalSubmitWrap">
          <div className="obFootNote">
            By submitting this form, you agree to Terms of use, Privacy policy.
            Your information is confidential and secure.
          </div>
          <button
            type="button"
            className="obBtn obBtnPrimary obBtnSubmitGlobal"
            onClick={handleGlobalSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "SUBMIT APPLICATION"}
          </button>
        </div>

        <div className="obSSL">
          <div className="obSSLBadge">
            <div className="obShield">
              <img className="ssllogo" src={badge} alt="Secure Connection Badge" />
            </div>
            <div>
              <div className="obSSLTitle">SECURE SSL <br />CONNECTION</div>
              <div className="obSSLSub">Verified & Secured</div>
            </div>
          </div>
        </div>
      </main>

      <footer className="obFooterBar">
        <p className="obFooterText">
          Merchant Marvels is an independent sales agent and is not a bank. Payment processing services are provided by Paysafe Payment Processing Solutions, LLC, a registered ISO of Citizens Bank, N.A., Providence, RI – Member FDIC.
        </p>
      </footer>
    </div>
  );
}

/* ─── step sub-components ─────────────────────────────────────── */

function Field({ label, hint, error, children }) {
  const child = React.cloneElement(children, { error });
  return (
    <div className={`obField ${error ? "hasError" : ""}`}>
      <div className="obLabel">{label}</div>
      <div className="obFieldRight">
        {child}
        {hint ? <div className="obHint">{hint}</div> : null}
        {error ? <div className="obError">{error}</div> : null}
      </div>
    </div>
  );
}

function Input({
  value, onChange, placeholder, type = "text", error,
  maxLength, inputMode, autoComplete, name, onBlur,
}) {
  return (
    <input
      className={`obInput ${error ? "obInputError" : ""}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      type={type}
      maxLength={maxLength}
      inputMode={inputMode}
      autoComplete={autoComplete}
      name={name}
    />
  );
}

function PasswordToggleInput({
  value, onChange, placeholder, error, maxLength, inputMode, autoComplete, name, onBlur,
}) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        className={`obInput ${error ? "obInputError" : ""}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        type={show ? "text" : "password"}
        maxLength={maxLength}
        inputMode={inputMode}
        autoComplete={autoComplete}
        name={name}
        style={{ width: "100%", paddingRight: "46px" }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Hide value" : "Show value"}
        style={{
          position: "absolute", right: "12px", top: "50%",
          transform: "translateY(-50%)", border: "none", background: "transparent",
          padding: 0, margin: 0, cursor: "pointer", fontSize: "18px",
          lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {show
          ? <img className="obInputIcon" src={eyeSlash} alt="Hide value" />
          : <img className="obInputIcon" src={eye} alt="Show value" />}
      </button>
    </div>
  );
}

function DobInput({ value, onChange, onBlur, error }) {
  const [focused, setFocused] = useState(false);
  const masked = (value || "").replace(/\d/g, "X");
  return (
    <input
      className={`obInput ${error ? "obInputError" : ""}`}
      value={focused ? (value || "") : (value ? masked : "")}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); onBlur?.(); }}
      placeholder="MM-DD-YYYY"
      inputMode={focused ? "numeric" : "text"}
      maxLength={10}
      autoComplete="bday"
    />
  );
}

function EinInput({ value, onChange, onBlur, error }) {
  const [focused, setFocused] = useState(false);
  /* show XX-XXXXX when blurred and has a value */
  const masked = value ? (value || "").replace(/\d/g, "X") : "";
  return (
    <input
      className={`obInput ${error ? "obInputError" : ""}`}
      value={focused ? (value || "") : masked}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); onBlur?.(); }}
      placeholder="00-0000000"
      inputMode={focused ? "numeric" : "text"}
      maxLength={10}
      autoComplete="off"
    />
  );
}

function Select({ value, onChange, options, placeholder = "Please select…", onBlur, name }) {
  return (
    <select
      className="obInput"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      name={name}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function QualifyHero() {
  return (
    <div className="obQualifyHero">
      <h1 className="obQualifyTitle">
        You qualify for <span>0% processing</span>
      </h1>
      <div className="obQualifyMeta">
        <div className="obSavingsWrap">
          <div className="obPiggy">
            <img src={piggyBank} alt="Piggy Bank" />
          </div>
          <div className="obSavingsLabel">
            <div>POTENTIAL</div>
            <div>Annual Savings</div>
          </div>
          <div className="obSavingsAmountWrap">
            <span className="obSavingsUp">up to</span>
            <span className="obSavingsAmount">$17 500</span>
          </div>
        </div>
        <div className="obQualifySub">
          Ready to eliminate processing fees forever? Here is the process
        </div>
      </div>
    </div>
  );
}

function StepBusiness({ form, errors, onChange, onBlurField }) {
  return (
    <div className="obGrid2">
      <Field label="Business name" error={errors.businessName}>
        <Input
          value={form.businessName}
          onChange={(v) => onChange({ businessName: v })}
          onBlur={() => onBlurField?.("businessName")}
          name="businessName"
          placeholder="Your business name"
        />
      </Field>
      <Field label="Business owner name" error={errors.businessOwnerName}>
        <Input
          value={form.businessOwnerName}
          onChange={(v) => onChange({ businessOwnerName: v })}
          onBlur={() => onBlurField?.("businessOwnerName")}
          name="businessOwnerName"
          placeholder="Your Name"
        />
      </Field>
      <Field label="Business email" error={errors.businessEmail}>
        <Input
          value={form.businessEmail}
          onChange={(v) => {
            const cleaned = v.replace(/[^A-Za-z0-9._+\-@]/g, "");
            onChange({ businessEmail: cleaned });
          }}
          onBlur={() => onBlurField?.("businessEmail")}
          name="businessEmail"
          placeholder="example@email.com"
          type="text"
        />
      </Field>
      <Field label="Business phone number" error={errors.businessPhone}>
        <Input
          value={form.businessPhone}
          onChange={(v) => onChange({ businessPhone: formatUSPhone(v) })}
          onBlur={() => onBlurField?.("businessPhone")}
          placeholder="(xxx) xxx-xxxx"
          inputMode="tel"
          autoComplete="tel"
          name="businessPhone"
          maxLength={16}
        />
      </Field>
    </div>
  );
}

function StepQualify({ form, errors, onChange, onBlurField, einSkip, onEinSkipChange }) {
  return (
    <div className="obGrid2">
      <Field label="Business legal name" error={errors.businessLegalName}>
        <Input
          value={form.businessLegalName}
          onChange={(v) => onChange({ businessLegalName: v })}
          onBlur={() => onBlurField?.("businessLegalName")}
          name="businessLegalName"
          placeholder="Your business legal name"
        />
      </Field>
      <Field
        label="DBA (Doing Business As)"
        error={errors.dbaName}
        hint="This name will appear on your receipt"
      >
        <Input
          value={form.dbaName}
          onChange={(v) => onChange({ dbaName: v })}
          onBlur={() => onBlurField?.("dbaName")}
          name="dbaName"
          placeholder="Your DBA name"
        />
      </Field>
      <Field label="Business entity type" error={errors.entityType}>
        <Select
          value={form.entityType}
          onChange={(v) => onChange({ entityType: v })}
          onBlur={() => onBlurField?.("entityType")}
          name="entityType"
          options={[
            { value: "llc", label: "LLC" },
            { value: "sole", label: "Sole Proprietorship" },
            { value: "corp", label: "Corporation" },
            { value: "partnership", label: "Partnership" },
            { value: "nonprofit", label: "Non-profit" },
          ]}
        />
      </Field>
      <Field label="Product/Service sold" error={errors.productServiceSold}>
        <Select
          value={form.productServiceSold}
          onChange={(v) => onChange({ productServiceSold: v })}
          onBlur={() => onBlurField?.("productServiceSold")}
          name="productServiceSold"
          options={[
            { value: "retail", label: "Retail" },
            { value: "restaurant", label: "Restaurant" },
            { value: "services", label: "Services" },
            { value: "other", label: "Other" },
          ]}
        />
      </Field>
      <Field
        label="Monthly Visa/MC/Discover/Amex volume"
        error={errors.annualVolume}
        hint="Estimated monthly credit card sales volume"
      >
        <Select
          value={form.annualVolume}
          onChange={(v) => onChange({ annualVolume: v })}
          onBlur={() => onBlurField?.("annualVolume")}
          name="annualVolume"
          options={[
            { value: "0-5k", label: "$0 – $5,000" },
            { value: "5k-10k", label: "$5,000 – $10,000" },
            { value: "10k-25k", label: "$10,000 – $25,000" },
            { value: "25k-50k", label: "$25,000 – $50,000" },
            { value: "50k+", label: "$50,000+" },
          ]}
          placeholder="Please select estimated volume"
        />
      </Field>
      <Field label="Average Purchase Order $" error={errors.avgSale}>
        <Input
          value={form.avgSale}
          onChange={(v) => {
            const cleaned = (v || "")
              .toString()
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*)\./g, "$1")
              .slice(0, 12);
            onChange({ avgSale: cleaned });
          }}
          onBlur={() => onBlurField?.("avgSale")}
          placeholder="$"
          inputMode="decimal"
          name="avgSale"
          maxLength={12}
        />
      </Field>
      <Field label="High Purchase Order $" hint="Largest single transaction amount you expect">
        <Input
          value={form.highPurchaseTicket}
          onChange={(v) => {
            const cleaned = (v || "")
              .toString()
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*)\./g, "$1")
              .slice(0, 12);
            onChange({ highPurchaseTicket: cleaned });
          }}
          onBlur={() => onBlurField?.("highPurchaseTicket")}
          placeholder="$"
          inputMode="decimal"
          name="highPurchaseTicket"
          maxLength={12}
        />
      </Field>
      <Field label="Business start date (Approx)" error={errors.yearStarted}>
        <Input
          value={form.yearStarted}
          onChange={(v) => onChange({ yearStarted: formatUSDate(v) })}
          onBlur={() => onBlurField?.("yearStarted")}
          placeholder="MM-DD-YYYY"
          inputMode="numeric"
          name="yearStarted"
          maxLength={10}
        />
      </Field>
      <Field label="Business address" error={errors.businessAddr1}>
        <Input
          value={form.businessAddr1}
          onChange={(v) => onChange({ businessAddr1: v })}
          onBlur={() => onBlurField?.("businessAddr1")}
          name="businessAddr1"
          placeholder="Street address"
        />
      </Field>
      <Field label="City" error={errors.city}>
        <Input
          value={form.city}
          onChange={(v) => onChange({ city: v })}
          onBlur={() => onBlurField?.("city")}
          name="city"
          placeholder=""
        />
      </Field>
      <Field label="State" error={errors.state}>
        <Select
          value={form.state}
          onChange={(v) => onChange({ state: v })}
          onBlur={() => onBlurField?.("state")}
          name="state"
          options={US_STATES}
          placeholder="Select state…"
        />
      </Field>
      <Field label="ZIP" error={errors.zip2}>
        <Input
          value={form.zip2}
          onChange={(v) => onChange({ zip2: formatZip(v) })}
          onBlur={() => onBlurField?.("zip2")}
          placeholder="00000"
          inputMode="numeric"
          name="zip2"
          maxLength={10}
        />
      </Field>

      {/* Federal Tax ID — masked input + tooltip */}
      <div className={`obField ${!einSkip && errors.ein ? "hasError" : ""}`}>
        <div className="obLabel">
          Federal Tax ID
          {!einSkip && (
            <span className="obTooltipWrap">
              <span className="obLockIcon">🔒</span>
              <span className="obTooltipBox">We use your Tax ID to verify your legal identity and comply with financial regulations</span>
            </span>
          )}
        </div>
        <div className="obFieldRight">
          {einSkip ? (
            <div className="obSkippedField">Skipped — add later with your rep</div>
          ) : (
            <EinInput
              value={form.ein}
              onChange={(v) => onChange({ ein: formatEIN(v) })}
              onBlur={() => onBlurField?.("ein")}
              error={errors.ein}
            />
          )}
          <label className="obSkipLabel">
            <input
              type="checkbox"
              className="obSkipCheckboxInput"
              checked={einSkip}
              onChange={(e) => onEinSkipChange(e.target.checked)}
            />
            <span>Don't have it handy? Skip for now</span>
          </label>
          {!einSkip && errors.ein && <div className="obError">{errors.ein}</div>}
        </div>
      </div>
    </div>
  );
}

function StepOwner({ form, errors, onChange, onBlurField }) {
  return (
    <div>
      <div className="obStepNotice obStepNoticeBlue">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        <span>The card networks require us to verify the person signing. This is identity verification only.</span>
      </div>
      <div className="obGrid2">
      <Field label="Owner First Name" error={errors.ownerFirstName}>
        <Input
          value={form.ownerFirstName}
          onChange={(v) => onChange({ ownerFirstName: v })}
          onBlur={() => onBlurField?.("ownerFirstName")}
          name="ownerFirstName"
          placeholder=""
        />
      </Field>
      <Field label="Owner Last Name" error={errors.ownerLastName}>
        <Input
          value={form.ownerLastName}
          onChange={(v) => onChange({ ownerLastName: v })}
          onBlur={() => onBlurField?.("ownerLastName")}
          name="ownerLastName"
          placeholder=""
        />
      </Field>
      <Field label="Title" error={errors.ownerTitle}>
        <Select
          value={form.ownerTitle}
          onChange={(v) => onChange({ ownerTitle: v })}
          onBlur={() => onBlurField?.("ownerTitle")}
          name="ownerTitle"
          options={[
            { value: "owner", label: "Owner" },
            { value: "ceo", label: "CEO" },
            { value: "president", label: "President" },
            { value: "manager", label: "Manager" },
            { value: "other", label: "Other" },
          ]}
          placeholder="Select title…"
        />
      </Field>
      <Field label="Email" error={errors.ownerEmail}>
        <Input
          value={form.ownerEmail}
          onChange={(v) => {
            const cleaned = v.replace(/[^A-Za-z0-9._+\-@]/g, "");
            onChange({ ownerEmail: cleaned });
          }}
          onBlur={() => onBlurField?.("ownerEmail")}
          name="ownerEmail"
          placeholder="example@email.com"
          type="text"
        />
      </Field>
      <Field label="Cell phone" error={errors.ownerPhone}>
        <Input
          value={form.ownerPhone}
          onChange={(v) => onChange({ ownerPhone: formatUSPhone(v) })}
          onBlur={() => onBlurField?.("ownerPhone")}
          placeholder="(xxx) xxx-xxxx"
          inputMode="tel"
          autoComplete="tel"
          name="ownerPhone"
          maxLength={16}
        />
      </Field>
      <Field
        label="Business Ownership %"
        error={errors.ownershipPercent}
        hint="Your ownership share in this business"
      >
        <Input
          value={form.ownershipPercent}
          onChange={(v) => onChange({ ownershipPercent: v })}
          onBlur={() => onBlurField?.("ownershipPercent")}
          name="ownershipPercent"
          placeholder=""
        />
      </Field>
      <Field label="Home address" error={errors.homeAddr1}>
        <Input
          value={form.homeAddr1}
          onChange={(v) => onChange({ homeAddr1: v })}
          onBlur={() => onBlurField?.("homeAddr1")}
          name="homeAddr1"
          placeholder="Street address"
        />
      </Field>
      <Field label="City" error={errors.homeCity}>
        <Input
          value={form.homeCity}
          onChange={(v) => onChange({ homeCity: v })}
          onBlur={() => onBlurField?.("homeCity")}
          name="homeCity"
          placeholder=""
        />
      </Field>
      <Field label="State" error={errors.homeState}>
        <Select
          value={form.homeState}
          onChange={(v) => onChange({ homeState: v })}
          onBlur={() => onBlurField?.("homeState")}
          name="homeState"
          options={US_STATES}
          placeholder="Select state…"
        />
      </Field>
      <Field label="ZIP" error={errors.homeZip}>
        <Input
          value={form.homeZip}
          onChange={(v) => onChange({ homeZip: formatZip(v) })}
          onBlur={() => onBlurField?.("homeZip")}
          placeholder="00000"
          inputMode="numeric"
          name="homeZip"
          maxLength={10}
        />
      </Field>

      {/* DOB — moved to bottom with masking + tooltip */}
      <div className={`obField ${errors.dob ? "hasError" : ""}`}>
        <div className="obLabel">
          Date of birth{" "}
          <span className="obTooltipWrap">
            <span className="obLockIcon">🔒</span>
            <span className="obTooltipBox">Your date of birth is used to verify your identity</span>
          </span>
        </div>
        <div className="obFieldRight">
          <DobInput
            value={form.dob}
            onChange={(v) => onChange({ dob: formatUSDate(v) })}
            onBlur={() => onBlurField?.("dob")}
            error={errors.dob}
          />
          {errors.dob && <div className="obError">{errors.dob}</div>}
        </div>
      </div>
    </div>
    </div>
  );
}

function StepBanking({ form, errors, onChange, onBlurField, bankingSkip, onBankingSkipChange }) {
  const [routingValid, setRoutingValid] = useState(null); // null | true | false

  const handleRoutingChange = (v) => {
    const d = formatRouting(v);
    onChange({ routingNumber: d });
    if (d.length === 9) {
      setRoutingValid(abaChecksum(d));
    } else {
      setRoutingValid(null);
    }
  };

  if (bankingSkip) {
    return (
      <div className="obBankingSkippedWrap">
        <div className="obBankingSkippedMsg">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4747a6" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>Banking details skipped — your rep will help you complete this before activation.</span>
        </div>
        <button
          type="button"
          className="obBankingSkipUndo"
          onClick={() => onBankingSkipChange(false)}
        >
          Add banking now instead
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="obBankingIntro">
        <h3 className="obBankingTitle">Where should we deposit your sales?</h3>
        <div className="obBankingReassurance">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
          <span>We only use this to deposit your card sales. Nothing is ever withdrawn outside a signed agreement.</span>
        </div>
      </div>

      <div className="obGrid2">
        {/* Routing first */}
        <div className={`obField ${errors.routingNumber ? "hasError" : ""}`}>
          <div className="obLabel">
            Routing Number
            <span className="obLockIcon" title="Your routing number is encrypted and secure">🔒</span>
          </div>
          <div className="obFieldRight">
            <Input
              value={form.routingNumber}
              onChange={handleRoutingChange}
              onBlur={() => onBlurField?.("routingNumber")}
              inputMode="numeric"
              name="routingNumber"
              maxLength={9}
              placeholder="9-digit ABA routing number"
              error={errors.routingNumber}
            />
            {form.routingNumber?.length === 9 && routingValid === true && (
              <div className="obRoutingOk">✓ Valid routing number</div>
            )}
            {errors.routingNumber && <div className="obError">{errors.routingNumber}</div>}
            <div className="obHint">Find on the bottom-left of your check</div>
          </div>
        </div>

        <Field label="Bank Name" error={errors.bankName}>
          <Input
            value={form.bankName}
            onChange={(v) => onChange({ bankName: v })}
            onBlur={() => onBlurField?.("bankName")}
            name="bankName"
            placeholder="e.g. Chase, Bank of America"
          />
        </Field>

        {/* Account number with lock icon */}
        <div className={`obField ${errors.accountNumber ? "hasError" : ""}`}>
          <div className="obLabel">
            Account Number
            <span className="obLockIcon" title="Masked for your security">🔒</span>
          </div>
          <div className="obFieldRight">
            <PasswordToggleInput
              value={form.accountNumber}
              onChange={(v) => onChange({ accountNumber: formatAccount(v) })}
              onBlur={() => onBlurField?.("accountNumber")}
              inputMode="numeric"
              autoComplete="off"
              name="accountNumber"
              maxLength={17}
              placeholder="Enter account number"
              error={errors.accountNumber}
            />
            {errors.accountNumber && <div className="obError">{errors.accountNumber}</div>}
          </div>
        </div>

        <div className={`obField ${errors.confirmAccountNumber ? "hasError" : ""}`}>
          <div className="obLabel">
            Confirm Account
            <span className="obLockIcon" title="Masked for your security">🔒</span>
          </div>
          <div className="obFieldRight">
            <PasswordToggleInput
              value={form.confirmAccountNumber}
              onChange={(v) => onChange({ confirmAccountNumber: formatAccount(v) })}
              onBlur={() => onBlurField?.("confirmAccountNumber")}
              inputMode="numeric"
              autoComplete="off"
              name="confirmAccountNumber"
              maxLength={17}
              placeholder="Re-enter account number"
              error={errors.confirmAccountNumber}
            />
            {errors.confirmAccountNumber && <div className="obError">{errors.confirmAccountNumber}</div>}
          </div>
        </div>
      </div>

      <div className="obBankingSkipRow">
        <button
          type="button"
          className="obBankingSkipLink"
          onClick={() => onBankingSkipChange(true)}
        >
          Add this later with my rep →
        </button>
      </div>
    </div>
  );
}

function StepSelectProduct({ selected, onSelect, error }) {
  const [openProduct, setOpenProduct] = useState(selected?.id ?? PRODUCTS[0]?.id);
  const toggle = (id) => setOpenProduct((prev) => (prev === id ? null : id));

  return (
    <div>
      <p className="obSub obAccSub" style={{ marginTop: 0, marginBottom: 20 }}>
        Choose a POS solution to continue. You can change your selection anytime before submission.
      </p>
      {error ? <div className="obServerError">{error}</div> : null}

      <div className="obProdAccordion">
        {PRODUCTS.map((p) => {
          const isOpen = openProduct === p.id;
          const isSelected = selected?.id === p.id;

          return (
            <div
              key={p.id}
              className={`obProdPanel${isOpen ? " isOpen" : ""}${isSelected ? " isSelected" : ""}`}
            >
              <button type="button" className="obProdHeader" onClick={() => toggle(p.id)}>
                <img className="obProdThumb" src={p.imageUrl} alt={p.name} />
                <div className="obProdHeaderInfo">
                  <span className="obProdHeaderName">{p.name}</span>
                  <span className="obProdHeaderPrice">
                    ${p.priceNow.toFixed(2)}
                    <span className="obProdHeaderPriceSub">/mo</span>
                    <span className="obProdHeaderWas">&nbsp;was ${p.priceWas.toFixed(2)}/mo</span>
                  </span>
                </div>
                {isSelected && <span className="obProdSelectedBadge">&#10003; Selected</span>}
                <span className="obProdChevron">{isOpen ? "▲" : "▼"}</span>
              </button>

              {isOpen && (
                <div className="obProdBody">
                  <div className="obProdSummaryHeading">{p.name} Summary</div>
                  <div className="obProdHighlights">
                    <img className="obProdHighlightImg" src={p.imageUrl} alt={p.name} />
                    <div className="obProdHighlightRight">
                      <div className="obProdHighlightLabel">Highlights with the current selection:</div>
                      <div className="obProdFreeBadge">
                        <span className="obProdFreeTag">FREE*</span>
                        {p.name}
                      </div>
                      <ul className="obProdSpecList">
                        {p.specs.map((spec, i) => (
                          <li key={i}>
                            <span className="obProdSpecCheck">&#10003;</span>
                            {spec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <table className="obPricingTable">
                    <thead>
                      <tr>
                        <th>Transaction Type:</th>
                        <th>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.pricing.map((row, i) => (
                        <tr key={i}>
                          <td>
                            <span className="obPricingLabel">{row.label}</span>
                            {row.showIcons && (
                              <span className="obCardIcons">
                                <span className="obCardIcon obCardVisa">VISA</span>
                                <span className="obCardIcon obCardMc">MC</span>
                                <span className="obCardIcon obCardDisc">DISC</span>
                                <span className="obCardIcon obCardAmex">AMEX</span>
                              </span>
                            )}
                          </td>
                          <td className="obPricingRate">{row.rate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <p className="obPricingNote">
                    Pricing valid only in the US. Review all documents in application preview section of signature page for full explanation of fees.
                  </p>

                  <div className="obProdSelectRow">
                    <button
                      type="button"
                      className={`obProdSelectBtn${isSelected ? " isSelected" : ""}`}
                      onClick={() => onSelect(p)}
                    >
                      {isSelected ? "✓ Selected" : "CONTINUE WITH SELECTION →"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── validation ─────────────────────────────────────────────── */
function validateStep(stepKey, form, product, opts = {}) {
  const { einSkip = false, bankingSkip = false } = opts;
  const e = {};
  if (stepKey === "business") {
    e.businessName = required(form.businessName);
    e.businessOwnerName = required(form.businessOwnerName);
    e.businessEmail = validEmail(form.businessEmail);
    e.businessPhone = validPhone(form.businessPhone);
  }
  if (stepKey === "qualify") {
    e.businessLegalName = required(form.businessLegalName);
    e.dbaName = required(form.dbaName);
    if (!einSkip) e.ein = validEIN(form.ein);
    e.entityType = required(form.entityType);
    e.productServiceSold = required(form.productServiceSold);
    e.annualVolume = required(form.annualVolume);
    e.avgSale = validMoney(form.avgSale, "Average sale");
    e.yearStarted = required(form.yearStarted);
    e.businessAddr1 = required(form.businessAddr1);
    e.city = required(form.city);
    e.state = required(form.state);
    e.zip2 = validZip(form.zip2);
  }
  if (stepKey === "owner") {
    e.ownerFirstName = required(form.ownerFirstName);
    e.ownerLastName = required(form.ownerLastName);
    e.ownerTitle = required(form.ownerTitle);
    e.dob = validDob(form.dob);
    e.ownerEmail = validEmail(form.ownerEmail);
    e.ownerPhone = validPhone(form.ownerPhone);
    e.ownershipPercent = validPercent(form.ownershipPercent);
    e.homeAddr1 = required(form.homeAddr1);
    e.homeCity = required(form.homeCity);
    e.homeState = required(form.homeState);
    e.homeZip = validZip(form.homeZip);
  }
  if (stepKey === "banking" && !bankingSkip) {
    e.bankName = required(form.bankName);
    e.routingNumber = validRouting(form.routingNumber);
    e.accountNumber = validAccount(form.accountNumber);
    e.confirmAccountNumber = validConfirmAccount(form.accountNumber, form.confirmAccountNumber);
  }
  if (stepKey === "selectProduct") {
    if (!product) e.product = "Please select a product to continue.";
  }
  Object.keys(e).forEach((k) => e[k] == null && delete e[k]);
  return e;
}

/* ─── US states ──────────────────────────────────────────────── */
const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];
